import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const ExampleCard = ({ example, orientation = 'default' }) => {
  const {
    id,
    versionId = 'v1',
    testimonial = {},
    photo,
    photoAlt,
    links = {}
  } = example;

  const [itineraryData, setItineraryData] = useState(null);
  const [interviewData, setInterviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const itineraryRef = doc(db, 'itineraries', id, 'versions', versionId);
        const interviewRef = doc(db, 'interviews', id);

        const [itinerarySnap, interviewSnap] = await Promise.all([
          getDoc(itineraryRef),
          getDoc(interviewRef).catch(() => null)
        ]);

        if (!itinerarySnap.exists()) {
          throw new Error('Example itinerary not available yet.');
        }

        if (!isMounted) return;

        setItineraryData(itinerarySnap.data());
        setInterviewData(interviewSnap?.exists() ? interviewSnap.data() : null);
      } catch (err) {
        if (!isMounted) return;
        setError(err?.message || 'Failed to load example itinerary.');
        setItineraryData(null);
        setInterviewData(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      isMounted = false;
    };
  }, [id, versionId]);

  const travelerContext = useMemo(() => (
    itineraryData?.travelerContext || itineraryData?.context || null
  ), [itineraryData]);

  const party = travelerContext?.party
    || interviewData?.partyProfile
    || interviewData?.profileSummary
    || null;

  const rawHomeBase = travelerContext?.homebase || null;
  const homeBaseArea = travelerContext?.homebaseArea || travelerContext?.homebaseMeta?.area || null;
  const dynamics = travelerContext?.partyInsights
    || travelerContext?.preferences
    || interviewData?.partyInsights
    || null;


  const displayHomeBase = useMemo(() => {
    const source = homeBaseArea || rawHomeBase || null;
    if (!source || typeof source !== 'string') return null;
    const withoutParens = source.replace(/\s*\(.*?\)\s*$/, '');
    const firstPart = withoutParens.split(',')[0];
    const cleaned = firstPart.trim();
    return cleaned.length > 0 ? cleaned : null;
  }, [homeBaseArea, rawHomeBase]);

  const tripLengthSummary = useMemo(() => {
    if (!itineraryData) return displayHomeBase ? `${displayHomeBase}` : null;

    const toArray = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      if (typeof value === 'object') return Object.values(value);
      return [];
    };

    const candidateDays = [
      ...toArray(itineraryData.plannerFull?.days || itineraryData.plannerFull?.ordered || null),
      ...toArray(itineraryData.plannerPreview?.days || itineraryData.plannerPreview?.ordered || null)
    ];

    if (candidateDays.length === 0) {
      if (Array.isArray(itineraryData.days)) {
        return itineraryData.days.length > 0 ? `${itineraryData.days.length} day${itineraryData.days.length === 1 ? '' : 's'}` : null;
      }
      return null;
    }

    const dayNumbers = new Set();
    candidateDays.forEach((day, index) => {
      if (!day) return;
      const number = day.profile?.dayIndex
        ?? day.dayIndex
        ?? day.dayNumber
        ?? index + 1;
      dayNumbers.add(number);
    });

    const dayCount = dayNumbers.size || candidateDays.length;
    if (dayCount <= 0) return null;

    if (dayCount === 1) {
      return '1 day experience';
    }

    const nightCount = Math.max(dayCount - 1, 1);
    const lengthText = `${dayCount} day${dayCount === 1 ? '' : 's'} / ${nightCount} night${nightCount === 1 ? '' : 's'}`;
    const locationText = displayHomeBase ? `based in ${displayHomeBase}` : null;
    return [lengthText, locationText].filter(Boolean).join(', ');
  }, [itineraryData, displayHomeBase]);

  const itineraryLink = links.itinerary || (id ? `/itinerary/${id}/${versionId}` : null);
  const interviewLink = links.interview || (id ? `/interview/review/${id}` : null);

  return (
    <article className="glass-card rounded-lg overflow-hidden flex flex-col">
      <div className={`flex flex-col lg:flex-row flex-1 ${orientation === 'reverse' ? 'lg:flex-row-reverse' : ''}`}>
        <div className="w-full lg:w-5/12 flex flex-col">
          <div className="relative w-full overflow-hidden bg-slate-900 aspect-[16/10] lg:aspect-[16/9]">
            {photo ? (
              <img
                src={photo}
                alt={photoAlt || testimonial?.author || 'Featured travelers'}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm uppercase tracking-[0.3em]">
                Photo coming soon
              </div>
            )}
          </div>

          {testimonial?.quote && (
            <div className="px-5 lg:px-6 pt-4 pb-2">
              <blockquote className="text-emerald-300 italic text-sm sm:text-base leading-relaxed text-center">
                “{testimonial.quote}”
              </blockquote>
            </div>
          )}
        </div>

        <div className="flex-1 px-5 lg:px-6 pt-2 pb-6 lg:pb-8 space-y-3">
          {loading && (
            <div className="text-white/60 text-sm">Loading itinerary snapshot…</div>
          )}

          {error && !loading && (
            <div className="bg-rose-500/20 border border-rose-500/40 text-rose-100 rounded-lg px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {(party || tripLengthSummary) && (
                <div className="space-y-1">
                  {party && (
                    <p className="text-white text-base font-semibold leading-relaxed">{party}</p>
                  )}
                  {tripLengthSummary && (
                    <p className="text-white text-sm leading-relaxed">{tripLengthSummary}</p>
                  )}
                </div>
              )}

              {dynamics && (
                <div className="space-y-2">
                  <div className="h-px bg-white/20" />
                  <p className="text-white text-sm leading-relaxed">{dynamics}</p>
                </div>
              )}

              <div className="pt-2 flex flex-col sm:flex-row sm:flex-wrap sm:gap-2 gap-2 justify-center">
                {interviewLink ? (
                  <Link to={interviewLink} className="px-4 py-2 rounded-full border border-white/20 text-white text-sm text-center hover:bg-white/10 transition min-w-[200px]">
                    View Interview Answers
                  </Link>
                ) : (
                  <span className="px-4 py-2 rounded-full border border-white/10 text-white/50 text-sm text-center min-w-[200px]">
                    Interview Coming Soon
                  </span>
                )}

                {itineraryLink ? (
                  <Link to={itineraryLink} className="px-4 py-2 rounded-full border border-white/20 text-white text-sm text-center hover:bg-white/10 transition min-w-[200px]">
                    View Full Itinerary
                  </Link>
                ) : (
                  <span className="px-4 py-2 rounded-full border border-white/10 text-white/50 text-sm text-center min-w-[200px]">
                    Itinerary Coming Soon
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </article>
  );
};

export default ExampleCard;
